import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkActions = ({ 
  selectedCount = 0, 
  onBulkDelete, 
  onBulkExport, 
  onBulkMove,
  onClearSelection,
  categories = []
}) => {
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetParent, setTargetParent] = useState('');

  if (selectedCount === 0) return null;

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedCount} selected categories? This action cannot be undone.`)) {
      onBulkDelete();
    }
    setIsActionsOpen(false);
  };

  const handleBulkExport = () => {
    onBulkExport();
    setIsActionsOpen(false);
  };

  const handleBulkMove = () => {
    setShowMoveModal(true);
    setIsActionsOpen(false);
  };

  const handleMoveSubmit = () => {
    onBulkMove(targetParent);
    setShowMoveModal(false);
    setTargetParent('');
  };

  const parentOptions = [
    { value: '', label: 'Move to Root Level' },
    ...// Only allow moving to level 1-2 categories
    categories?.filter(cat => cat?.level <= 2)?.map(cat => ({
        value: cat?.id,
        label: `${'  '?.repeat(cat?.level - 1)}${cat?.name} (Level ${cat?.level})`
      }))
  ];

  return (
    <>
      {/* Bulk Actions Bar */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-card border border-border rounded-lg shadow-modal px-4 py-3 flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Check" size={16} color="white" />
            </div>
            <span className="text-sm font-medium text-foreground">
              {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="h-4 w-px bg-border" />

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleBulkExport}
            >
              Export
            </Button>

            <Button
              variant="outline"
              size="sm"
              iconName="Move"
              onClick={handleBulkMove}
            >
              Move
            </Button>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                iconName="MoreHorizontal"
                onClick={() => setIsActionsOpen(!isActionsOpen)}
              />

              {isActionsOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-90"
                    onClick={() => setIsActionsOpen(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-2 w-48 bg-popover border border-border rounded-lg shadow-modal z-100">
                    <div className="py-2">
                      <button
                        onClick={handleBulkDelete}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-destructive hover:bg-muted transition-smooth"
                      >
                        <Icon name="Trash2" size={16} />
                        <span>Delete Selected</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClearSelection}
            />
          </div>
        </div>
      </div>
      {/* Move Modal */}
      {showMoveModal && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-90"
            onClick={() => setShowMoveModal(false)}
          />
          <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg border border-border shadow-modal w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    Move Categories
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => setShowMoveModal(false)}
                  />
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Move {selectedCount} selected categories to a new parent category.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Target Parent Category
                    </label>
                    <select
                      value={targetParent}
                      onChange={(e) => setTargetParent(e?.target?.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {parentOptions?.map(option => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                    <Button
                      variant="outline"
                      onClick={() => setShowMoveModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleMoveSubmit}
                      iconName="Move"
                      iconPosition="left"
                    >
                      Move Categories
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BulkActions;